import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, formatApiError } from "@/lib/api";
import { Plus, X, MapPin, Sparkles, Loader2, Globe, Lock } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function TripBuilder() {
    const nav = useNavigate();
    const [name, setName] = useState("");
    const [travelers, setTravelers] = useState(2);
    const [budget, setBudget] = useState("mid");
    const [stops, setStops] = useState([{ destination: "", days: 3 }, { destination: "", days: 3 }]);
    const [notes, setNotes] = useState("");
    const [isPublic, setIsPublic] = useState(true);
    const [busy, setBusy] = useState(false);

    const updateStop = (i, k, v) => setStops((cur) => cur.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
    const addStop = () => stops.length < 6 && setStops((cur) => [...cur, { destination: "", days: 3 }]);
    const removeStop = (i) => stops.length > 1 && setStops((cur) => cur.filter((_, idx) => idx !== i));

    const submit = async (e) => {
        e.preventDefault();
        const clean = stops.filter((s) => s.destination.trim().length > 1);
        if (!name.trim() || clean.length === 0) {
            toast.error("Add a trip name and at least one destination.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/trips", {
                name: name.trim(),
                travelers,
                budget_level: budget,
                stops: clean.map((s) => ({ destination: s.destination.trim(), days: Number(s.days) || 3 })),
                notes,
                public: isPublic,
            });
            toast.success("Trip created!");
            nav(`/trip/${data.slug}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="App">
            <Toaster position="top-center" richColors closeButton />
            <Navbar />

            <section className="mx-auto max-w-4xl px-6 pt-12">
                <span className="overline">multi-city itinerary</span>
                <h1 className="mt-2 font-display text-5xl text-ink md:text-6xl">Plan a multi-stop trip.</h1>
                <p className="mt-3 max-w-2xl text-moss">
                    Add up to 6 destinations. We&apos;ll cost-estimate each stop, suggest hotels,
                    restaurants and activities, then bundle it into a beautiful shareable itinerary.
                </p>
            </section>

            <form onSubmit={submit} data-testid="trip-form" className="mx-auto mt-12 max-w-4xl px-6 pb-10 space-y-6">
                <div className="glass rounded-3xl p-6 md:p-8 space-y-4">
                    <label className="block">
                        <span className="overline">trip name</span>
                        <input
                            data-testid="trip-name"
                            type="text"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="My Mediterranean dream"
                            className="mt-2 w-full rounded-2xl bg-white px-4 py-3 ring-1 ring-jade/15 focus:outline-none focus:ring-terracotta/40"
                        />
                    </label>

                    <div className="grid gap-4 md:grid-cols-3">
                        <label className="block">
                            <span className="overline">travelers</span>
                            <input
                                data-testid="trip-travelers"
                                type="number" min="1" max="20"
                                value={travelers}
                                onChange={(e) => setTravelers(Number(e.target.value || 1))}
                                className="mt-2 w-full rounded-2xl bg-white px-4 py-3 ring-1 ring-jade/15 focus:outline-none focus:ring-terracotta/40"
                            />
                        </label>
                        <label className="block">
                            <span className="overline">budget</span>
                            <select
                                data-testid="trip-budget"
                                value={budget}
                                onChange={(e) => setBudget(e.target.value)}
                                className="mt-2 w-full rounded-2xl bg-white px-4 py-3 ring-1 ring-jade/15 focus:outline-none focus:ring-terracotta/40"
                            >
                                <option value="budget">Budget</option>
                                <option value="mid">Mid-range</option>
                                <option value="luxury">Luxury</option>
                            </select>
                        </label>
                        <label className="flex items-end gap-3">
                            <button
                                type="button"
                                onClick={() => setIsPublic((p) => !p)}
                                data-testid="trip-visibility"
                                className={`pill w-full justify-center py-3 ${isPublic ? "bg-jade text-sand" : "bg-ink/10 text-ink"}`}
                            >
                                {isPublic ? <><Globe size={14} className="mr-2" /> Public</> : <><Lock size={14} className="mr-2" /> Private</>}
                            </button>
                        </label>
                    </div>
                </div>

                <div className="space-y-3">
                    {stops.map((s, i) => (
                        <div key={i} data-testid={`trip-stop-${i}`} className="glass rounded-2xl p-4 md:p-5 grid gap-3 md:grid-cols-[2fr_1fr_auto] items-center">
                            <label className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 ring-1 ring-jade/10 focus-within:ring-terracotta/40">
                                <span className="grid h-8 w-8 place-items-center rounded-full bg-terracotta/10 text-terracotta text-sm font-medium">{i + 1}</span>
                                <MapPin size={14} className="text-jade" />
                                <input
                                    data-testid={`stop-destination-${i}`}
                                    type="text"
                                    required={i === 0}
                                    placeholder="Stop destination (city, region…)"
                                    value={s.destination}
                                    onChange={(e) => updateStop(i, "destination", e.target.value)}
                                    className="w-full bg-transparent outline-none"
                                />
                            </label>
                            <label className="flex items-center gap-2 rounded-2xl bg-white px-4 py-3 ring-1 ring-jade/10">
                                <span className="text-xs uppercase tracking-[0.18em] text-moss/70">Days</span>
                                <input
                                    data-testid={`stop-days-${i}`}
                                    type="number" min="1" max="30"
                                    value={s.days}
                                    onChange={(e) => updateStop(i, "days", Number(e.target.value || 1))}
                                    className="w-full bg-transparent outline-none"
                                />
                            </label>
                            <button
                                type="button"
                                onClick={() => removeStop(i)}
                                disabled={stops.length === 1}
                                data-testid={`stop-remove-${i}`}
                                aria-label="Remove stop"
                                className="grid h-12 w-12 place-items-center rounded-full bg-white text-moss transition hover:bg-destructive/10 hover:text-destructive disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    ))}
                    {stops.length < 6 && (
                        <button
                            type="button"
                            onClick={addStop}
                            data-testid="trip-add-stop"
                            className="pill w-full justify-center border-2 border-dashed border-jade/30 bg-white/40 py-4 text-jade hover:bg-jade hover:text-sand hover:border-jade"
                        >
                            <Plus size={14} className="mr-2" /> Add another stop
                        </button>
                    )}
                </div>

                <label className="block">
                    <span className="overline">notes (optional)</span>
                    <textarea
                        data-testid="trip-notes"
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Any special requirements, anniversaries, dietary needs…"
                        className="mt-2 w-full rounded-2xl bg-white px-4 py-3 ring-1 ring-jade/15 focus:outline-none focus:ring-terracotta/40"
                    />
                </label>

                <div className="flex flex-wrap items-center gap-3">
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="trip-submit"
                        className="btn-primary !px-7 !py-4"
                    >
                        {busy ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Building your trip…</>) : (<><Sparkles size={16} className="mr-2" /> Build my itinerary</>)}
                    </button>
                    <span className="text-xs text-moss/70">First-time generation can take ~30–60s per new destination.</span>
                </div>
            </form>

            <Footer />
        </div>
    );
}
